'use client'

import {
  Box,
  Button,
  Checkbox,
  Input,
  InputGroup,
  Stack,
  Text,
} from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { Controller } from 'react-hook-form'
import { Eye, EyeOff, Lock, Mail, UserRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppField } from '@/shared'
import { useRegisterForm } from '../model/use-register-form'

export function RegisterForm() {
  const { t } = useTranslation()
  const { form, onSubmit } = useRegisterForm()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    control,
    formState: { errors, isSubmitting },
  } = form

  return (
    <form noValidate onSubmit={onSubmit}>
      <Stack gap={4}>
        <AppField.Root invalid={!!errors.fullName}>
          <AppField.Label fontSize="sm" color="fg.muted">
            {t('auth.register.fullName')}
          </AppField.Label>
          <InputGroup startElement={<UserRound size={16} />}>
            <Input
              {...register('fullName')}
              type="text"
              placeholder={t('auth.register.fullNamePlaceholder')}
              size="md"
              bg="bg.surface"
              borderColor={errors.fullName ? 'primary.fg' : 'border'}
              borderRadius="lg"
            />
          </InputGroup>
          <AppField.ErrorText>{errors.fullName?.message}</AppField.ErrorText>
        </AppField.Root>

        <AppField.Root invalid={!!errors.email}>
          <AppField.Label fontSize="sm" color="fg.muted">
            {t('auth.register.email')}
          </AppField.Label>
          <InputGroup startElement={<Mail size={16} />}>
            <Input
              {...register('email')}
              type="email"
              placeholder={t('auth.login.emailPlaceholder')}
              size="md"
              bg="bg.surface"
              borderColor={errors.email ? 'primary.fg' : 'border'}
              borderRadius="lg"
            />
          </InputGroup>
          <AppField.ErrorText>{errors.email?.message}</AppField.ErrorText>
        </AppField.Root>

        <AppField.Root invalid={!!errors.password}>
          <AppField.Label fontSize="sm" color="fg.muted">
            {t('auth.register.password')}
          </AppField.Label>
          <InputGroup
            startElement={<Lock size={16} />}
            endElement={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                minW="auto"
                px={1}
                color="fg.muted"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? t('auth.register.hidePassword')
                    : t('auth.register.showPassword')
                }
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </Button>
            }
          >
            <Input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.register.passwordPlaceholder')}
              size="md"
              bg="bg.surface"
              borderColor={errors.password ? 'primary.fg' : 'border'}
              borderRadius="lg"
            />
          </InputGroup>
          <AppField.ErrorText>{errors.password?.message}</AppField.ErrorText>
        </AppField.Root>

        <AppField.Root invalid={!!errors.confirmPassword}>
          <AppField.Label fontSize="sm" color="fg.muted">
            {t('auth.register.confirmPassword')}
          </AppField.Label>
          <InputGroup
            startElement={<Lock size={16} />}
            endElement={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                minW="auto"
                px={1}
                color="fg.muted"
                onClick={() => setShowConfirmPassword((current) => !current)}
                aria-label={
                  showConfirmPassword
                    ? t('auth.register.hideConfirmPassword')
                    : t('auth.register.showConfirmPassword')
                }
              >
                {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </Button>
            }
          >
            <Input
              {...register('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t('auth.register.confirmPlaceholder')}
              size="md"
              bg="bg.surface"
              borderColor={errors.confirmPassword ? 'primary.fg' : 'border'}
              borderRadius="lg"
            />
          </InputGroup>
          <AppField.ErrorText>
            {errors.confirmPassword?.message}
          </AppField.ErrorText>
        </AppField.Root>

        <AppField.Root invalid={!!errors.termsAccepted}>
          <Controller
            name="termsAccepted"
            control={control}
            render={({ field }) => (
              <Checkbox.Root
                checked={field.value}
                onCheckedChange={(event) =>
                  field.onChange(event.checked === true)
                }
                colorPalette="red"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control
                  borderColor="primary.fg"
                  cursor="pointer"
                  mt={0.5}
                />
                <Checkbox.Label fontSize="sm" color="fg.muted">
                  {t('auth.register.terms')}
                </Checkbox.Label>
              </Checkbox.Root>
            )}
          />
          <AppField.ErrorText>
            {errors.termsAccepted?.message}
          </AppField.ErrorText>
        </AppField.Root>

        <Button
          mt={2}
          type="submit"
          size="md"
          bg="primary.solid"
          color="white"
          borderRadius="lg"
          _hover={{ opacity: 0.88 }}
          _active={{ opacity: 0.72 }}
          loading={isSubmitting}
          loadingText={t('auth.register.creating')}
        >
          {t('auth.register.createAccount')}
        </Button>

        <Text fontSize="sm" color="fg.muted" textAlign="center">
          {t('auth.register.haveAccount')}{' '}
          <Box as="span" color="primary.fg" fontWeight={600}>
            <Link to="/auth/login" style={{ color: 'inherit' }}>
              {t('auth.register.signIn')}
            </Link>
          </Box>
        </Text>
      </Stack>
    </form>
  )
}
